import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { useNotification } from './NotificationContext'
import githubService from '../services/githubService'
import { flushLoginAttribution } from '../services/loginAttribution'
import { apiClient, setGlobalSessionInvalidate } from '../utils/apiClient'
import { isPublicRecordingPath } from '../utils/publicRoutes'

interface User {
  id: number
  /** Numeric GitHub account id (from JWT /api/user). */
  github_id: number
  login: string
  name: string
  avatar_url: string
  email: string
  organization_id?: number
  is_admin?: boolean
  has_github_app_access?: boolean
  github_app_installations?: string[]
  github_app_install_url?: string
  terms_accepted?: boolean
  current_terms_version?: string
  terms_accepted_version?: string
  subscription_tier?: 'paygo' | 'team' | 'org'
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  acceptTerms: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { showNotification } = useNotification()

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('github_access_token')
      if (token) {
        // Fetch user info from backend
        const userData = await apiClient.get<{ 
          user_id: string; 
          github_id: number;
          username: string;
          organization_id?: number;
          is_admin?: boolean;
          has_github_app_access?: boolean;
          github_app_installations?: string[];
          github_app_install_url?: string;
          terms_accepted?: boolean;
          current_terms_version?: string;
          terms_accepted_version?: string;
          subscription_tier?: 'paygo' | 'team' | 'org';
        }>('/api/user', {
          'Authorization': `Bearer ${token}`,
        })

        const user: User = {
          id: Number(userData.user_id),
          github_id: userData.github_id,
          login: userData.username,
          name: userData.username,
          avatar_url: `https://avatars.githubusercontent.com/u/${userData.github_id}?v=4`,
          email: `${userData.username}@example.com`,
          organization_id: userData.organization_id,
          is_admin: userData.is_admin,
          has_github_app_access: userData.has_github_app_access,
          github_app_installations: userData.github_app_installations,
          github_app_install_url: userData.github_app_install_url,
          terms_accepted: userData.terms_accepted,
          current_terms_version: userData.current_terms_version,
          terms_accepted_version: userData.terms_accepted_version,
          subscription_tier: userData.subscription_tier || 'paygo'
        }
        setUser(user)
        await flushLoginAttribution()
      } else {
        setUser(null)
      }
    } catch (error) {
      console.error('Auth check error:', error)
      console.warn('Invalid token detected, clearing authentication')
      githubService.logout()
      setUser(null)
      const onPublicRecording = typeof window !== 'undefined' && isPublicRecordingPath(window.location.pathname)
      if (sessionStorage.getItem('veilstream_suppress_auth_check_toast_once')) {
        sessionStorage.removeItem('veilstream_suppress_auth_check_toast_once')
      } else if (!onPublicRecording) {
        showNotification('Your session has expired. Please log in again.', 'warning')
      }
    } finally {
      setIsLoading(false)
    }
  }, [showNotification])

  const login = async () => {
    try {
      await githubService.initiateOAuth()
    } catch (error) {
      console.error('Login initiation error:', error)
      showNotification('Failed to start GitHub authentication. Please try again.', 'error')
      throw error // Re-throw to let the Login component handle it
    }
  }

  const invalidateSessionSilently = useCallback(() => {
    githubService.logout()
    setUser(null)
  }, [])

  const logout = () => {
    try {
      invalidateSessionSilently()
      showNotification('Successfully logged out.', 'info')
    } catch (error) {
      console.error('Logout error:', error)
      setUser(null)
    }
  }

  const acceptTerms = async () => {
    try {
      const token = localStorage.getItem('github_access_token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      await apiClient.post('/api/user/accept-terms', {}, {
        'Authorization': `Bearer ${token}`,
      })

      // Refresh user data to get updated terms status
      await checkAuth()
      showNotification('Terms and conditions accepted successfully.', 'success')
    } catch (error) {
      console.error('Accept terms error:', error)
      showNotification('Failed to accept terms and conditions. Please try again.', 'error')
      throw error
    }
  }

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    setGlobalSessionInvalidate(invalidateSessionSilently)
  }, [invalidateSessionSilently])

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuth,
    acceptTerms,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
